import { inject, Injectable, Provider } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { EFavoriteGetQueryType, EFavoriteType, FavoriteDto, FavoriteGetQueryResult, ProductIdentifierDto } from '../../INTEGRA_Shop_Client';
import { LoggerService } from '../../infrastructure/logger/logger.service';
import { IFavoriteApiService, IFavoriteApiServiceToken } from '../../api/favorite/favorite-api-service.interface';
import { IFavoriteService, IFavoriteServiceToken } from './favorite-service.interface';

@Injectable({
  providedIn: 'root'
})
export class FavoriteService implements IFavoriteService {
  private readonly logger: LoggerService;
  private readonly favoriteApiService: IFavoriteApiService;

  constructor() {
    this.logger = inject(LoggerService);
    this.favoriteApiService = inject(IFavoriteApiServiceToken);
  }

  public favoriteGet(favoriteGetQueryType: EFavoriteGetQueryType): Observable<FavoriteDto[]> {
    return this.favoriteApiService.swagFavoriteGet(favoriteGetQueryType).pipe(
      mergeMap(
        (result: FavoriteGetQueryResult) => {
          let favoriteDtoResultList = result.favorites;
          if (!favoriteDtoResultList) {
            throw new Error("Failure upon extracting the favorites from the favoriteGetResult.");
          }
          return of(favoriteDtoResultList);
        }
      )
    ).pipe(
      catchError(
        (er) => {
          this.logger.error(`${er}`, this.constructor.name);
          throw new Error('Failure upon loading the favorites.');
        }
      )
    );
  }

  public favoriteSet(productIdentifierList: ProductIdentifierDto[], favoriteType?: EFavoriteType | undefined): Observable<boolean> {
    return this.favoriteApiService.swagFavoriteSet(productIdentifierList, favoriteType).pipe(
      mergeMap(
        () => {
          return of(true);
        }
      )
    ).pipe(
      catchError(
        (er) => {
          this.logger.error(`${er}`, this.constructor.name);
          throw new Error('Failure upon saving favorites.');
        }
      )
    );
  }

  public favoriteDelete(productIdentifierList: ProductIdentifierDto[], favoriteType?: EFavoriteType | undefined): Observable<boolean> {
    return this.favoriteApiService.swagFavoriteDelete(productIdentifierList, favoriteType).pipe(
      mergeMap(
        () => {
          return of(true);
        }
      )
    ).pipe(
      catchError(
        (er) => {
          this.logger.error(`${er}`, this.constructor.name);
          throw new Error('Failure upon deleting favorites.');
        }
      )
    );
  }
}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const FavoriteServiceProvider: Provider[] = [
  { provide: IFavoriteServiceToken, useExisting: FavoriteService }
];
